import { DateTime } from "luxon";
import type { FileRejection } from "react-dropzone";
import type { ReportUploadedFile } from "@/api/report/_models/ReportUpload";
import { BUSINESS_TIME_ZONE } from "@/components/snapshot/snapshot.utils";
import type { SnapshotUploadFile } from "@/components/snapshot/useSnapshotUpload";

/** The day's files already in S3 (GET /reports/{report}/uploads). */
export interface SnapshotDropzoneExistingFiles {
  files: ReportUploadedFile[];
  isLoading: boolean;
  error: unknown;
}

/** The file types a Snapshot drop zone accepts: .xls, .xlsx, .csv. */
export const REPORT_SNAPSHOT_DROPZONE_ACCEPT: Record<string, string[]> = {
  "application/vnd.ms-excel": [".xls"],
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
  "text/csv": [".csv"],
};

/** A file size as "840 KB" / "12.3 MB". */
export const toFileSizeText = (bytes: number): string => {
  if (bytes < 1_000_000) return `${Math.max(1, Math.round(bytes / 1_000))} KB`;
  return `${(bytes / 1_000_000).toFixed(1)} MB`;
};

/** An ISO time as "20 Oct 2026, 13:02" in Sydney. */
export const toSydneyDateTimeText = (iso: string): string => {
  return DateTime.fromISO(iso).setZone(BUSINESS_TIME_ZONE).toFormat("d LLL yyyy, HH:mm");
};

/** A file's extension, upper-case, for its badge ("XLSX", "CSV"); "FILE" when it has none. */
export const toFileExtensionLabel = (filename: string): string => {
  const dot = filename.lastIndexOf(".");
  if (dot < 0) return "FILE";
  return filename.slice(dot + 1).toUpperCase();
};

/** A file badge's tone: CSV green, Excel blue, anything else grey. */
export type FileExtensionTone = "success" | "info" | "neutral";

/** The badge tone for a file name. */
export const toFileExtensionTone = (filename: string): FileExtensionTone => {
  const extension = toFileExtensionLabel(filename);
  if (extension === "CSV") return "success";
  if (extension === "XLS" || extension === "XLSX") return "info";
  return "neutral";
};

/** The "Not added" line for files the drop zone refused; null when none. */
export const toRejectionText = (rejections: FileRejection[]): string | null => {
  if (rejections.length === 0) return null;
  const names = rejections.map((r) => r.file.name).join(", ");
  return `Not added (only .xls, .xlsx, .csv): ${names}`;
};

/** A file uploaded here as an uploaded S3 entry, always under the name it was dropped with. */
export const toReportUploadedFileFromItem = (
  item: SnapshotUploadFile,
  key: string,
  listed: ReportUploadedFile | undefined,
): ReportUploadedFile => {
  return {
    id: listed?.id ?? key.split("/").pop() ?? key,
    key,
    filename: item.file.name,
    size: listed?.size ?? item.file.size,
    uploadedAt: listed?.uploadedAt ?? item.uploadedAt ?? new Date().toISOString(),
  };
};
